import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Bloodtype as BloodTypeIcon,
  CalendarToday as CalendarIcon,
  FamilyRestroom as FamilyIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  Medication as MedicationIcon,
  LocalHospital as HospitalIcon,
  AccessTime as TimeIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon,
  MedicalServices as MedicalIcon,
  Note as NoteIcon,
} from '@mui/icons-material';
import { PatientProfile } from '../types/patient';

interface PatientProfileDialogProps {
  open: boolean;
  onClose: () => void;
  profile: PatientProfile | null;
  loading?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

/**
 * Patient Profile Dialog Component
 * Shows complete patient information for doctor view before visit.
 *
 * @author Civilhealth Development Team
 */
const PatientProfileDialog: React.FC<PatientProfileDialogProps> = ({
  open,
  onClose,
  profile,
  loading = false,
}) => {
  const [activeTab, setActiveTab] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!profile && !loading) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'transparent' , color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MedicalIcon />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Patient Profile
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {profile?.bookingNumber && `Appointment: ${profile.bookingNumber}`}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {loading ? (
        <Box sx={{ p: 3 }}>
          <LinearProgress />
          <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading patient profile...</Typography>
        </Box>
      ) : (
        <DialogContent sx={{ p: 0 }}>
          {/* Patient Type Banner */}
          {profile && (
            <Box sx={{ p: 2, bgcolor: profile.patientType === 'FAMILY_MEMBER' ? '#e8f5e9' : '#e3f2fd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {profile.patientType === 'FAMILY_MEMBER' ? (
                  <>
                    <FamilyIcon sx={{ color: '#2e7d32' }} />
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                      Family Member - {profile.relationshipDisplayName}
                    </Typography>
                  </>
                ) : (
                  <>
                    <PersonIcon sx={{ color: '#1565c0' }} />
                    <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                      Officer (Self)
                    </Typography>
                  </>
                )}
                <Chip
                  label={profile.appointmentType || 'Home Visit'}
                  size="small"
                  sx={{ ml: 'auto', bgcolor: 'white' }}
                />
                {profile.priority && profile.priority !== 'Normal' && (
                  <Chip
                    label={profile.priority}
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: '1px solid #e0e0e0', bgcolor: 'transparent' }}>
            <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
              <Tab icon={<PersonIcon />} label="Personal Info" iconPosition="start" />
              <Tab icon={<MedicalIcon />} label="Medical Info" iconPosition="start" />
              <Tab icon={<HistoryIcon />} label="Visit History" iconPosition="start" />
              <Tab icon={<MedicationIcon />} label="Prescriptions" iconPosition="start" />
            </Tabs>
          </Box>

          {profile && (
            <Box sx={{ p: 2 }}>
              {/* Tab 1: Personal Information */}
              <TabPanel value={activeTab} index={0}>
                <Grid container spacing={2}>
                  {/* Basic Information Card */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ width: 64, height: 64, bgcolor: 'transparent' , fontSize: '1.5rem' }}>
                          {profile.name?.charAt(0) || 'P'}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {profile.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip size="small" label={profile.gender || 'N/A'} />
                            <Chip size="small" label={`${profile.age || 'N/A'} years`} />
                            {profile.bloodGroup && (
                              <Chip size="small" icon={<BloodTypeIcon />} label={profile.bloodGroup} color="error" />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">Contact</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile.mobile || 'N/A'}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">Email</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile.email || 'N/A'}</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Address Card */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon fontSize="small" />
                        Address
                      </Typography>
                      <Typography variant="body1">
                        {profile.fullAddress || 'Address not provided'}
                      </Typography>
                      {profile.landmark && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Landmark: {profile.landmark}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

                  {/* Officer Information (for family members) */}
                  {profile.patientType === 'FAMILY_MEMBER' && profile.officerName && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'transparent' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" />
                          Related Officer
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Name</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>{profile.officerName}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Designation</Typography>
                            <Typography variant="body1">{profile.officerDesignation || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Department</Typography>
                            <Typography variant="body1">{profile.officerDepartment || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Contact</Typography>
                            <Typography variant="body1">{profile.officerMobile || 'N/A'}</Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )}

                  {/* Appointment Context */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'transparent' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" />
                        Appointment Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="text.secondary">Date</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{profile.appointmentDate}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="text.secondary">Time</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{profile.appointmentTime || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Type</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{profile.appointmentType || 'Home Visit'}</Typography>
                        </Grid>
                        {profile.reasonForVisit && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">Reason for Visit</Typography>
                            <Typography variant="body1">{profile.reasonForVisit}</Typography>
                          </Grid>
                        )}
                        {profile.specialNotes && (
                          <Grid item xs={12}>
                            <Alert severity="info" sx={{ mt: 1 }}>
                              <Typography variant="subtitle2">Special Notes</Typography>
                              <Typography variant="body2">{profile.specialNotes}</Typography>
                            </Alert>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Tab 2: Medical Information */}
              <TabPanel value={activeTab} index={1}>
                <Grid container spacing={2}>
                  {/* Medical History */}
                  {profile.medicalHistory && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'transparent' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, color: '#2e7d32', fontWeight: 600 }}>
                          <HistoryIcon fontSize="small" />
                          Medical History
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{profile.medicalHistory}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Emergency Notes */}
                  {profile.emergencyNotes && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'transparent' , border: '2px solid #ff9800' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <WarningIcon sx={{ color: '#ff9800' }} />
                          <Typography variant="subtitle2" sx={{ color: '#ff9800', fontWeight: 600 }}>Emergency Notes</Typography>
                        </Box>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{profile.emergencyNotes}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Allergies */}
                  {profile.allergies && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'transparent' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <WarningIcon color="error" />
                          <Typography variant="subtitle2" color="error">Allergies</Typography>
                        </Box>
                        <Typography variant="body1">{profile.allergies}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Chronic Conditions */}
                  {profile.chronicConditions && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <HospitalIcon fontSize="small" />
                          Chronic Conditions
                        </Typography>
                        <Typography variant="body1">{profile.chronicConditions}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Current Medications */}
                  {profile.currentMedications && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MedicationIcon fontSize="small" />
                          Current Medications
                        </Typography>
                        <Typography variant="body1">{profile.currentMedications}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Emergency Contact */}
                  {(profile.emergencyContactName || profile.emergencyContactPhone || profile.emergencyContactRelation || profile.emergencyContactEmail) && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'transparent' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon fontSize="small" />
                          Emergency Contact
                        </Typography>
                        <Grid container spacing={2}>
                          {profile.emergencyContactName && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">Name</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>{profile.emergencyContactName}</Typography>
                            </Grid>
                          )}
                          {profile.emergencyContactRelation && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">Relation</Typography>
                              <Typography variant="body1">{profile.emergencyContactRelation}</Typography>
                            </Grid>
                          )}
                          {profile.emergencyContactPhone && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">Phone</Typography>
                              <Typography variant="body1">{profile.emergencyContactPhone}</Typography>
                            </Grid>
                          )}
                          {profile.emergencyContactEmail && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">Email</Typography>
                              <Typography variant="body1">{profile.emergencyContactEmail}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    </Grid>
                  )}

                  {/* Chief Complaints from Appointment */}
                  {profile.chiefComplaints && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AssignmentIcon fontSize="small" />
                          Chief Complaints (This Visit)
                        </Typography>
                        <Typography variant="body1">{profile.chiefComplaints}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* Symptoms */}
                  {profile.symptoms && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Symptoms</Typography>
                        <Typography variant="body1">{profile.symptoms}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* No Medical Info */}
                  {!profile.medicalHistory && !profile.emergencyNotes && !profile.allergies && !profile.chronicConditions && !profile.currentMedications && !profile.chiefComplaints && (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <MedicalIcon sx={{ fontSize: 48, color: '#dadce0', mb: 1 }} />
                        <Typography color="text.secondary">No medical information available</Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </TabPanel>

              {/* Tab 3: Visit History */}
              <TabPanel value={activeTab} index={2}>
                {profile.visitHistory && profile.visitHistory.length > 0 ? (
                  <List>
                    {profile.visitHistory.map((visit, index) => (
                      <React.Fragment key={index}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: 'transparent' , color: '#2e7d32' }}>
                              <AssignmentIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {visit.doctorName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ({visit.doctorSpecialization || 'General'})
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {visit.visitDate} • {visit.visitType}
                                </Typography>
                                {visit.vitalSigns && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    <strong>Vitals:</strong> {visit.vitalSigns}
                                  </Typography>
                                )}
                                {visit.chiefComplaints && (
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Complaints: {visit.chiefComplaints}
                                  </Typography>
                                )}
                                {visit.diagnosis && (
                                  <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                                    Diagnosis: {visit.diagnosis}
                                  </Typography>
                                )}
                                {visit.treatmentPlan && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Treatment: {visit.treatmentPlan}
                                  </Typography>
                                )}
                                {visit.prescriptions && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Prescribed: {visit.prescriptions}
                                  </Typography>
                                )}
                                {visit.followUpDate && (
                                  <Chip
                                    size="small"
                                    label={`Follow-up: ${visit.followUpDate}`}
                                    sx={{ mt: 0.5 }}
                                  />
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < profile.visitHistory.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <HistoryIcon sx={{ fontSize: 48, color: '#dadce0', mb: 1 }} />
                    <Typography color="text.secondary">No previous visit history available</Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Tab 4: Prescription History */}
              <TabPanel value={activeTab} index={3}>
                {profile.prescriptionHistory && profile.prescriptionHistory.length > 0 ? (
                  <List>
                    {profile.prescriptionHistory.map((prescription, index) => (
                      <React.Fragment key={index}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: 'transparent' , color: '#1565c0' }}>
                              <MedicationIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {prescription.doctorName}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={`${prescription.medicationsCount || 0} medicines`}
                                  sx={{ bgcolor: 'transparent' }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Date: {prescription.prescriptionDate}
                                </Typography>
                                {prescription.diagnosis && (
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Diagnosis: {prescription.diagnosis}
                                  </Typography>
                                )}
                                {prescription.medications && (
                                  <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                                    Medicines: {prescription.medications}
                                  </Typography>
                                )}
                                {prescription.validUntil && (
                                  <Chip
                                    size="small"
                                    label={`Valid until: ${prescription.validUntil}`}
                                    sx={{ mt: 0.5 }}
                                  />
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < profile.prescriptionHistory.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <MedicationIcon sx={{ fontSize: 48, color: '#dadce0', mb: 1 }} />
                    <Typography color="text.secondary">No prescription history available</Typography>
                  </Box>
                )}
              </TabPanel>
            </Box>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default PatientProfileDialog;